import React, { createContext, useContext, useMemo, useState, useCallback, useEffect, useRef } from "react";
import { GOOGLE_MAPS_CONFIG } from "./googleMapsConfig";

const GoogleMapsContext = createContext(null);

export const useGoogleMaps = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
  }

  // Trigger lazy load when a consumer accesses the hook
  const { requestLoad, isLoaded, loadError } = context;
  useEffect(() => {
    requestLoad();
  }, [requestLoad]);

  return { isLoaded, loadError };
};

/**
 * Lazy GoogleMapsProvider — does NOT load the Google Maps SDK until
 * a component calls useGoogleMaps(). This avoids downloading ~200KB
 * of Maps JS on pages that don't use maps.
 */
export const GoogleMapsProvider = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const loadRequestedRef = useRef(false);
  const loadingRef = useRef(false);

  const requestLoad = useCallback(() => {
    if (loadRequestedRef.current) return; // already requested
    loadRequestedRef.current = true;

    // If already loaded by another instance / previous mount
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    if (loadingRef.current) return; // script already being appended
    loadingRef.current = true;

    const { googleMapsApiKey, libraries, version, language, region } = GOOGLE_MAPS_CONFIG;
    const libs = libraries?.join(",") || "";
    const src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=${libs}&v=${version}&language=${language}&region=${region}`;

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsLoaded(true);
      loadingRef.current = false;
    };
    script.onerror = (e) => {
      setLoadError(e);
      loadingRef.current = false;
    };
    document.head.appendChild(script);
  }, []);

  const contextValue = useMemo(
    () => ({ isLoaded, loadError, requestLoad }),
    [isLoaded, loadError, requestLoad]
  );

  return (
    <GoogleMapsContext.Provider value={contextValue}>
      {children}
    </GoogleMapsContext.Provider>
  );
};